import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiLayers,
  FiUsers,
  FiHome,
  FiBookOpen,
  FiCalendar,
  FiGrid,
  FiList,
  FiStar,
  FiClock,
  FiPlus,
  FiSearch,
  FiExternalLink,
} from "react-icons/fi";
import "./ResearchTab.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Tab definitions
const sectionTabs = [
  { id: "rooms", label: "Research Spaces", icon: FiLayers },
  { id: "communities", label: "Communities", icon: FiUsers },
  { id: "workshops", label: "Workshops", icon: FiBookOpen },
];

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    className="rsc-stat-card"
    variants={itemVariants}
    style={{ "--stat-color": color }}
    whileHover={{ y: -2 }}
  >
    <div className="rsc-stat-icon">
      <Icon />
    </div>
    <div className="rsc-stat-info">
      <span className="rsc-stat-value">{value}</span>
      <span className="rsc-stat-label">{label}</span>
    </div>
  </motion.div>
);

// Research Room Card - CLICKABLE with navigation
const RoomCard = ({ room, getFieldImage, viewMode }) => {
  const formatDate = (date) => {
    if (!date) return "Recently";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const isCreator = room.isCreator || room.isLeader || room.role === "leader" || room.userRole === "creator";

  // Photo priority: signedPhotoURL > photoURL > field-based fallback
  const getPhotoSrc = () => {
    if (room.signedPhotoURL) return room.signedPhotoURL;
    if (room.photoURL) return room.photoURL;
    return getFieldImage(room.field);
  };

  return (
    <Link to={room.previewUrl || `/labs/${room._id || room.id}/preview`} className="rsc-card-link">
      <motion.div
        className={`rsc-room-card ${viewMode}`}
        variants={itemVariants}
        whileHover={{ y: -4 }}
      >
        <div className="rsc-room-image">
          <img
            src={getPhotoSrc()}
            alt={room.field || "Research"}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/field-images/default.jpg";
            }}
          />
          <div className="rsc-room-overlay">
            <span className="rsc-room-field">{room.field || "Research"}</span>
            {isCreator && (
              <span className="rsc-room-badge creator">
                <FiStar /> Creator
              </span>
            )}
          </div>
        </div>
        <div className="rsc-room-content">
          <h3 className="rsc-room-title">{room.name || room.title}</h3>
          <p className="rsc-room-desc">
            {room.description?.substring(0, viewMode === "grid" ? 80 : 150) ||
              "Active research collaboration space"}
            {room.description?.length > (viewMode === "grid" ? 80 : 150) && "..."}
          </p>
          <div className="rsc-room-meta">
            <span className="rsc-room-members">
              <FiUsers /> {room.memberCount || room.members?.length || 0} members
            </span>
            <span className="rsc-room-date">
              <FiCalendar /> {formatDate(room.createdAt)}
            </span>
          </div>
          {room.tags?.length > 0 && (
            <div className="rsc-room-tags">
              {room.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="rsc-room-tag">
                  #{tag}
                </span>
              ))}
              {room.tags.length > 3 && (
                <span className="rsc-room-tag-more">+{room.tags.length - 3}</span>
              )}
            </div>
          )}
        </div>
        <div className="rsc-room-action">
          <FiExternalLink />
        </div>
      </motion.div>
    </Link>
  );
};

// Community Card - CLICKABLE with navigation
const CommunityCard = ({ community, getCommunityImage, viewMode }) => {
  const formatDate = (date) => {
    if (!date) return "Recently joined";
    return `Joined ${new Date(date).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })}`;
  };

  // Photo priority: signedPhotoURL > photoURL > fallback
  const getPhotoSrc = () => {
    if (community.signedPhotoURL) return community.signedPhotoURL;
    if (community.photoURL) return community.photoURL;
    return getCommunityImage(community);
  };

  const isCreator = community.userRole === 'creator' || community.role === 'creator';
  const isModerator = community.userRole === 'moderator' || community.role === 'moderator';

  return (
    <Link to={community.previewUrl || `/communities/${community._id || community.id}/preview`} className="rsc-card-link">
      <motion.div
        className={`rsc-community-card ${viewMode}`}
        variants={itemVariants}
        whileHover={{ y: -4 }}
      >
        <div className="rsc-community-image">
          <img
            src={getPhotoSrc()}
            alt={community.name}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/field-images/default.jpg";
            }}
          />
          {(isCreator || isModerator) && (
            <span className={`rsc-community-badge ${isCreator ? 'creator' : 'moderator'}`}>
              {isCreator ? 'Creator' : 'Mod'}
            </span>
          )}
        </div>
        <div className="rsc-community-content">
          <h3 className="rsc-community-title">{community.name}</h3>
          <p className="rsc-community-desc">
            {community.description?.substring(
              0,
              viewMode === "grid" ? 60 : 120
            ) || "Research community"}
            {community.description?.length > (viewMode === "grid" ? 60 : 120) &&
              "..."}
          </p>
          <div className="rsc-community-meta">
            <span className="rsc-community-members">
              <FiUsers />{" "}
              {community.memberCount || community.members?.length || 0} members
            </span>
            <span className="rsc-community-joined">
              <FiClock /> {formatDate(community.joinedAt)}
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

// Workshop Card - CLICKABLE with navigation (no photos supported)
const WorkshopCard = ({ workshop, viewMode }) => {
  const status = workshop.status || (() => {
    if (!workshop.startDate) return "upcoming";
    const start = new Date(workshop.startDate);
    const end = workshop.endDate ? new Date(workshop.endDate) : null;
    const now = new Date();
    if (now < start) return "upcoming";
    if (end && now > end) return "completed";
    return "ongoing";
  })();

  const isOrganizer =
    workshop.isOrganizer ||
    workshop.role === "organizer" ||
    workshop.userRole === "organizer" ||
    workshop.isCoOrganizer ||
    workshop.userRole === "co-organizer";

  const formatDate = (date) => {
    if (!date) return "TBD";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Link to={workshop.previewUrl || `/workshops/${workshop._id || workshop.id}`} className="rsc-card-link">
      <motion.div
        className={`rsc-workshop-card ${viewMode}`}
        variants={itemVariants}
        whileHover={{ y: -4 }}
      >
        <div className="rsc-workshop-header">
          <div className="rsc-workshop-badges">
            <span className={`rsc-workshop-status ${status}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
            {isOrganizer && (
              <span className="rsc-workshop-role organizer">Organizer</span>
            )}
          </div>
          <h3 className="rsc-workshop-title">{workshop.title}</h3>
        </div>
        <div className="rsc-workshop-content">
          <p className="rsc-workshop-desc">
            {workshop.description?.substring(0, viewMode === "grid" ? 80 : 120) || "Professional workshop"}
            {workshop.description?.length > (viewMode === "grid" ? 80 : 120) && "..."}
          </p>
        </div>
        <div className="rsc-workshop-meta">
          <span className="rsc-workshop-date">
            <FiCalendar /> {formatDate(workshop.startDate)}
          </span>
          {workshop.field && (
            <span className="rsc-workshop-field">
              <FiLayers /> {workshop.field}
            </span>
          )}
          {workshop.participantCount > 0 && (
            <span className="rsc-workshop-participants">
              <FiUsers /> {workshop.participantCount}
            </span>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

// Empty State
const EmptyState = ({ type, isOwnProfile }) => {
  const config = {
    rooms: {
      icon: FiLayers,
      title: "No research spaces yet",
      text: isOwnProfile
        ? "Create or join a research space to collaborate with others"
        : "This user hasn't joined any research spaces yet",
    },
    communities: {
      icon: FiUsers,
      title: "No communities joined",
      text: isOwnProfile
        ? "Join communities to connect with like-minded researchers"
        : "This user hasn't joined any communities yet",
    },
    workshops: {
      icon: FiBookOpen,
      title: "No workshops",
      text: isOwnProfile
        ? "Organize or join workshops to share and learn"
        : "This user hasn't participated in any workshops yet",
    },
  };

  const { icon: Icon, title, text } = config[type];

  return (
    <motion.div className="rsc-empty" variants={itemVariants}>
      <div className="rsc-empty-icon">
        <Icon />
      </div>
      <h3 className="rsc-empty-title">{title}</h3>
      <p className="rsc-empty-text">{text}</p>
      {isOwnProfile && (
        <motion.button
          className="rsc-empty-btn"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <FiPlus />
          <span>
            {type === "rooms"
              ? "Create Space"
              : type === "communities"
              ? "Find Communities"
              : "Browse Workshops"}
          </span>
        </motion.button>
      )}
    </motion.div>
  );
};

// Main Component
const ResearchTab = ({
  researchRooms = [],
  communities = [],
  workshops = [],
  stats,
  isOwnProfile,
  getFieldImage,
  getCommunityImage,
}) => {
  const [activeSection, setActiveSection] = useState("rooms");
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");

  // Calculate stats
  const computedStats = useMemo(() => {
    const createdRooms = researchRooms.filter(
      (r) => r.isCreator || r.isLeader || r.role === "leader"
    ).length;

    return [
      {
        icon: FiLayers,
        label: "Research Spaces",
        value: researchRooms.length,
        color: "var(--prf-blue)",
      },
      {
        icon: FiHome,
        label: "Created",
        value: createdRooms,
        color: "var(--prf-purple)",
      },
      {
        icon: FiUsers,
        label: "Communities",
        value: communities.length,
        color: "var(--prf-green)",
      },
      {
        icon: FiBookOpen,
        label: "Workshops",
        value: workshops.length,
        color: "var(--prf-amber)",
      },
    ];
  }, [researchRooms, communities, workshops]);

  // Filter content based on search
  const filteredContent = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return {
      rooms: researchRooms.filter(
        (r) =>
          !query ||
          r.name?.toLowerCase().includes(query) ||
          r.title?.toLowerCase().includes(query) ||
          r.field?.toLowerCase().includes(query)
      ),
      communities: communities.filter(
        (c) =>
          !query ||
          c.name?.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query)
      ),
      workshops: workshops.filter(
        (w) =>
          !query ||
          w.title?.toLowerCase().includes(query) ||
          w.field?.toLowerCase().includes(query)
      ),
    };
  }, [researchRooms, communities, workshops, searchQuery]);

  const currentItems = filteredContent[activeSection];

  return (
    <motion.div
      className="rsc-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Stats Overview */}
      <div className="rsc-stats">
        {computedStats.map((stat) => (
          <StatsCard key={stat.label} {...stat} />
        ))}
      </div>

      {/* Toolbar */}
      <motion.div className="rsc-toolbar" variants={itemVariants}>
        {/* Section Tabs */}
        <div className="rsc-tabs">
          {sectionTabs.map((tab) => {
            const Icon = tab.icon;
            const count = filteredContent[tab.id]?.length || 0;
            return (
              <motion.button
                key={tab.id}
                className={`rsc-tab ${
                  activeSection === tab.id ? "active" : ""
                }`}
                onClick={() => setActiveSection(tab.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="rsc-tab-icon" />
                <span>{tab.label}</span>
                <span className="rsc-tab-count">{count}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="rsc-toolbar-actions">
          {/* Search */}
          <div className="rsc-search">
            <FiSearch className="rsc-search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rsc-search-input"
            />
          </div>

          {/* View Toggle */}
          <div className="rsc-view-toggle">
            <motion.button
              className={`rsc-view-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              whileTap={{ scale: 0.95 }}
            >
              <FiGrid />
            </motion.button>
            <motion.button
              className={`rsc-view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              whileTap={{ scale: 0.95 }}
            >
              <FiList />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        className={`rsc-content ${viewMode}`}
        variants={containerVariants}
        key={activeSection}
      >
        {currentItems.length === 0 ? (
          <EmptyState type={activeSection} isOwnProfile={isOwnProfile} />
        ) : (
          <>
            {activeSection === "rooms" &&
              currentItems.map((room) => (
                <RoomCard
                  key={room._id || room.id}
                  room={room}
                  getFieldImage={getFieldImage}
                  viewMode={viewMode}
                />
              ))}
            {activeSection === "communities" &&
              currentItems.map((community) => (
                <CommunityCard
                  key={community._id || community.id}
                  community={community}
                  getCommunityImage={getCommunityImage}
                  viewMode={viewMode}
                />
              ))}
            {activeSection === "workshops" &&
              currentItems.map((workshop) => (
                <WorkshopCard
                  key={workshop._id || workshop.id}
                  workshop={workshop}
                  viewMode={viewMode}
                />
              ))}
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ResearchTab;
